import React from 'react';
import { Modal, Backdrop, CircularProgress, Box, Typography, LinearProgress, useTheme } from '@mui/material';
import { keyframes } from '@mui/system';

// Pulse animation for the dots
const pulse = keyframes`
  0%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 1;
  }
`;

// Wave animation for bars
const wave = keyframes`
  0%, 100% {
    transform: scaleY(0.5);
  }
  50% {
    transform: scaleY(1);
  }
`;

// Bounce animation
const bounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
`;

// Spinning animation for custom loader
const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// Fade in/out animation
const fadeInOut = keyframes`
  0%, 100% {
    opacity: 0.3;
  }
  50% {
    opacity: 1;
  }
`;

/**
 * Enhanced Loading Backdrop Component with Theme Integration
 * 
 * @param {boolean} open - Controls visibility of the backdrop
 * @param {string} message - Optional loading message to display
 * @param {number} zIndex - Optional z-index value (default: 9999)
 * @param {string} variant - Loading animation style: 
 *   'circular' | 'dots' | 'bars' | 'bounce' | 'spinner' | 'progress' | 'pulse'
 * @param {string|object} color - Color theme name from theme OR custom color object
 * @param {object} customColors - Override with custom color object
 */
const LoadingBackdrop = ({
  open = false,
  message = 'Loading...',
  zIndex = 9999,
  variant, // If not provided, will use theme default
  color, // If not provided, will use theme default
  customColors = null
}) => {
  const theme = useTheme();

  // Get default values from theme if not provided
  const finalVariant = variant || theme?.loadingBackdrop?.defaultVariant || 'spinner';
  const finalColor = color || theme?.loadingBackdrop?.defaultColor || 'primary';

  // Get colors from theme or use fallback
  const getColorsFromTheme = () => {
    // Priority: customColors > color object > theme colors > fallback
    if (customColors) {
      return customColors;
    }

    if (typeof finalColor === 'object') {
      return finalColor;
    }

    // Try to get from theme
    if (theme?.loadingBackdrop?.colors?.[finalColor]) {
      return theme.loadingBackdrop.colors[finalColor];
    }

    // Fallback color configurations
    const fallbackColors = {
      primary: {
        main: theme?.palette?.primary?.main || '#1976d2',
        light: theme?.palette?.primary?.light || '#42a5f5',
        text: '#fff'
      },
      secondary: {
        main: theme?.palette?.secondary?.main || '#dc004e',
        light: theme?.palette?.secondary?.light || '#e91e63',
        text: '#fff'
      },
      success: {
        main: theme?.palette?.success?.main || '#2e7d32',
        light: theme?.palette?.success?.light || '#66bb6a',
        text: '#fff'
      },
      warning: {
        main: theme?.palette?.warning?.main || '#ed6c02',
        light: theme?.palette?.warning?.light || '#ff9800',
        text: '#fff'
      },
      error: {
        main: theme?.palette?.error?.main || '#d32f2f',
        light: theme?.palette?.error?.light || '#f44336',
        text: '#fff'
      },
      white: {
        main: '#fff',
        light: '#f5f5f5',
        text: '#fff'
      }
    };

    return fallbackColors[finalColor] || fallbackColors.primary;
  };

  const colors = getColorsFromTheme();

  // Render different loading animations based on variant
  const renderLoader = () => {
    // Helper to apply color - works with both solid colors and gradients
    const getColorStyle = (colorValue) => {
      if (typeof colorValue === 'string' && colorValue.includes('gradient')) {
        return { background: colorValue };
      }
      return { backgroundColor: colorValue };
    };

    switch (finalVariant) {
      case 'circular':
        return <CircularProgress color="inherit" size={60} />;

      case 'dots':
        return (
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  ...getColorStyle(colors.main),
                  animation: `${pulse} 1.4s ease-in-out ${index * 0.2}s infinite`,
                }}
              />
            ))}
          </Box>
        );

      case 'bars':
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: 40 }}>
            {[0, 1, 2, 3, 4].map((index) => (
              <Box
                key={index}
                sx={{
                  width: 6,
                  height: 40,
                  ...getColorStyle(colors.main),
                  borderRadius: 1,
                  animation: `${wave} 1s ease-in-out ${index * 0.1}s infinite`,
                }}
              />
            ))}
          </Box>
        );

      case 'bounce':
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  ...getColorStyle(colors.main),
                  animation: `${bounce} 0.6s ease-in-out ${index * 0.1}s infinite`,
                }}
              />
            ))}
          </Box>
        );

      case 'spinner':
        return (
          <Box
            sx={{
              position: 'relative',
              width: 80,
              height: 80,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                border: '8px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '50%',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                border: '8px solid transparent',
                borderTopColor: typeof colors.main === 'string' && colors.main.includes('gradient')
                  ? colors.light
                  : colors.main,
                borderRadius: '50%',
                animation: `${spin} 1s linear infinite`,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 50,
                height: 50,
                border: '6px solid transparent',
                borderTopColor: colors.light,
                borderRadius: '50%',
                animation: `${spin} 0.7s linear infinite reverse`,
              }}
            />
          </Box>
        );

      case 'progress':
        return (
          <Box sx={{ width: 300 }}>
            <LinearProgress
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 3,
                  background: typeof colors.main === 'string' && colors.main.includes('gradient')
                    ? colors.main
                    : colors.main,
                }
              }}
            />
          </Box>
        );

      case 'pulse':
        return (
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              ...getColorStyle(colors.main),
              animation: `${fadeInOut} 1.5s ease-in-out infinite`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                ...getColorStyle(colors.light),
                animation: `${fadeInOut} 1.5s ease-in-out 0.3s infinite`,
              }}
            />
          </Box>
        );

      default:
        return <CircularProgress color="inherit" size={60} />;
    }
  };

  return (
    <Modal
      open={open}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
      }}
    >
      <Backdrop
        sx={{
          color: colors.text,
          zIndex: zIndex,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          backdropFilter: 'blur(4px)',
        }}
        open={open}
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            padding: 4,
            borderRadius: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          }}
        >
          {renderLoader()}

          {message && (
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 500,
                textAlign: 'center',
                maxWidth: '80vw',
                letterSpacing: '0.5px',
              }}
            >
              {message}
            </Typography>
          )}
        </Box>
      </Backdrop>
    </Modal>
  );
};

export default LoadingBackdrop;